// POLineEditor.jsx
// Inline editor for PO line items
// Shows vendor's mapped products first, with option to add from master catalog

import React, { Component } from 'react';
import { Form, Modal, Button } from 'react-bootstrap';
import VendorAPI from '../../../services/vendorService';
import ProductSelector from '../../partial/ProductSelector';
import { formatProductLabel } from '../../../util/productLabel';

class POLineEditor extends Component {
    state = {
        vendorProducts: [],
        selectedVendorProductId: '',
        selectedProduct: null, // resolved product info

        // Master catalog modal (for adding new products to vendor)
        showMasterModal: false,
        masterSelection: null,
        addProductForm: {
            vendor_sku: '',
            unit_cost: '',
            order_unit_id: '',
            unit_quantity: '',
            minimum_order_qty: ''
        },

        quantity_ordered: '',
        unit_cost: '',
        notes: '',

        loading: true,
        saving: false
    };

    async componentDidMount() {
        await this.loadVendorProducts();
    }

    loadVendorProducts = async () => {
        const { vendorId } = this.props;
        if (!vendorId) {
            this.setState({ loading: false });
            return;
        }
        try {
            const raw = await VendorAPI.getVendorProducts(vendorId);
            const vendorProducts = (Array.isArray(raw) ? raw : [])
                .map(vp => ({ ...vp, vendor_product_id: vp.vendor_product_id || vp.id }))
                .sort((a, b) => {
                    const nameA = formatProductLabel(a);
                    const nameB = formatProductLabel(b);
                    return nameA.localeCompare(nameB);
                });
            this.setState({ vendorProducts, loading: false });
        } catch (err) {
            console.error('Error loading vendor products:', err);
            this.setState({ loading: false });
        }
    };

    handleVendorProductChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({ showMasterModal: true });
            return;
        }

        const { vendorProducts } = this.state;
        const vp = vendorProducts.find(p => String(p.vendor_product_id) === value);

        if (vp) {
            this.setState({
                selectedVendorProductId: value,
                selectedProduct: vp,
                unit_cost: vp.unit_cost ? String(vp.unit_cost) : ''
            }, () => {
                if (this.qtyInputRef) this.qtyInputRef.focus();
            });
        } else {
            this.setState({
                selectedVendorProductId: '',
                selectedProduct: null,
                unit_cost: ''
            });
        }
    };

    // Master catalog modal handlers
    handleMasterSelection = (selection) => {
        this.setState({ masterSelection: selection });
    };

    handleAddFromMaster = async () => {
        const { masterSelection, addProductForm } = this.state;
        if (!masterSelection || !masterSelection.product_id) return;

        this.setState({ saving: true });
        try {
            const { vendorId } = this.props;
            await VendorAPI.addVendorProduct(vendorId, {
                master_product_id: masterSelection.product_id,
                vendor_sku: addProductForm.vendor_sku || null,
                unit_cost: addProductForm.unit_cost ? parseFloat(addProductForm.unit_cost) : null,
                order_unit_id: addProductForm.order_unit_id ? parseInt(addProductForm.order_unit_id, 10) : null,
                unit_quantity: addProductForm.unit_quantity ? parseFloat(addProductForm.unit_quantity) : null,
                minimum_order_qty: addProductForm.minimum_order_qty ? parseInt(addProductForm.minimum_order_qty, 10) : null,
                preferred: false
            });

            // Reload vendor products and select the newly added one
            await this.loadVendorProducts();
            const { vendorProducts } = this.state;
            // Find the just-added product
            const newVp = vendorProducts.find(vp =>
                String(vp.master_product_id) === String(masterSelection.product_id) &&
                (vp.vendor_sku || '') === (addProductForm.vendor_sku || '')
            );

            this.setState({
                showMasterModal: false,
                masterSelection: null,
                addProductForm: { vendor_sku: '', unit_cost: '', order_unit_id: '', unit_quantity: '', minimum_order_qty: '' },
                saving: false,
                selectedVendorProductId: newVp ? String(newVp.vendor_product_id) : '',
                selectedProduct: newVp || null,
                unit_cost: newVp && newVp.unit_cost ? String(newVp.unit_cost) : ''
            });
        } catch (err) {
            console.error('Error adding vendor product:', err);
            alert(err.message || 'Failed to add product to vendor.');
            this.setState({ saving: false });
        }
    };

    handleSave = () => {
        const { selectedProduct, quantity_ordered, unit_cost, notes } = this.state;
        if (!selectedProduct || !quantity_ordered) return;

        this.props.onSave({
            vendor_product_id: selectedProduct.vendor_product_id,
            master_product_id: selectedProduct.master_product_id || selectedProduct.product_id,
            quantity_ordered: parseInt(quantity_ordered, 10),
            unit_cost: unit_cost ? parseFloat(unit_cost) : null,
            notes: notes || null
        });
    };

    render() {
        const { onCancel, saving: parentSaving, installerId } = this.props;
        const { vendorProducts, selectedVendorProductId, selectedProduct, loading,
                showMasterModal, masterSelection, addProductForm, saving,
                quantity_ordered, unit_cost, notes } = this.state;

        return (
            <>
                <tr style={{ background: '#f8fafc' }}>
                    <td>
                        {loading ? (
                            <i className="fa-sharp fa-regular fa-spinner fa-spin"></i>
                        ) : (
                            <Form.Control as="select" size="sm"
                                value={selectedVendorProductId}
                                onChange={this.handleVendorProductChange}
                                style={{ minWidth: '220px' }}>
                                <option value="">Select a product...</option>
                                {vendorProducts.map(vp => (
                                    <option key={vp.vendor_product_id} value={String(vp.vendor_product_id)}>
                                        {formatProductLabel(vp)}{vp.vendor_sku ? ` \u2014 ${vp.vendor_sku}` : ''}{vp.unit_quantity ? ` (${vp.unit_quantity})` : ''}
                                    </option>
                                ))}
                                <option value="add_new">+ Add New Product</option>
                            </Form.Control>
                        )}
                    </td>
                    <td>{selectedProduct?.vendor_sku || '—'}</td>
                    <td>
                        <Form.Control type="number" size="sm" min="1"
                            ref={(ref) => { this.qtyInputRef = ref; }}
                            value={quantity_ordered}
                            onChange={(e) => this.setState({ quantity_ordered: e.target.value })}
                            style={{ width: '80px' }} />
                    </td>
                    <td>—</td>
                    <td>
                        <Form.Control type="number" size="sm" step="0.01" min="0"
                            value={unit_cost}
                            onChange={(e) => this.setState({ unit_cost: e.target.value })}
                            style={{ width: '100px' }} />
                    </td>
                    <td>{quantity_ordered && unit_cost ? `$${(parseFloat(quantity_ordered) * parseFloat(unit_cost)).toFixed(2)}` : '—'}</td>
                    <td>
                        <Form.Control type="text" size="sm"
                            value={notes}
                            onChange={(e) => this.setState({ notes: e.target.value })}
                            placeholder="Notes"
                            style={{ width: '120px' }} />
                    </td>
                    <td>
                        <button onClick={this.handleSave}
                            disabled={parentSaving || !selectedProduct || !quantity_ordered}
                            style={{ background: 'none', border: 'none', color: '#3FB2BE', fontSize: '12px', cursor: 'pointer', marginRight: '8px' }}>
                            {parentSaving ? <i className="fa-sharp fa-regular fa-spinner fa-spin"></i> : 'Save'}
                        </button>
                        <button onClick={onCancel}
                            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer' }}>
                            Cancel
                        </button>
                    </td>
                </tr>

                {/* Add Product from Master Catalog */}
                <Modal show={showMasterModal} onHide={() => this.setState({ showMasterModal: false, masterSelection: null })} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="fa-sharp fa-regular fa-cubes mr-2"></i>
                            Add Product to Vendor
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ProductSelector
                            ref={(ref) => { this.productSelectorRef = ref; }}
                            installerId={installerId}
                            useMasterCatalog={true}
                            onChange={this.handleMasterSelection}
                        />

                        {masterSelection && masterSelection.product_name && (
                            <>
                                <hr />
                                <h6 className="mb-3">Vendor Details for {formatProductLabel(masterSelection)}</h6>

                                <Form.Group className="mb-3">
                                    <Form.Label>Vendor SKU</Form.Label>
                                    <Form.Control type="text"
                                        value={addProductForm.vendor_sku}
                                        onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, vendor_sku: e.target.value } }))} />
                                </Form.Group>

                                <div className="d-flex" style={{ gap: '12px' }}>
                                    <Form.Group className="mb-3 flex-fill">
                                        <Form.Label>Unit Cost</Form.Label>
                                        <Form.Control type="number" step="0.01" min="0"
                                            value={addProductForm.unit_cost}
                                            onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, unit_cost: e.target.value } }))} />
                                    </Form.Group>
                                    <Form.Group className="mb-3 flex-fill">
                                        <Form.Label>Min Order Qty</Form.Label>
                                        <Form.Control type="number" min="0"
                                            value={addProductForm.minimum_order_qty}
                                            onChange={(e) => this.setState(prev => ({ addProductForm: { ...prev.addProductForm, minimum_order_qty: e.target.value } }))} />
                                    </Form.Group>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showMasterModal: false, masterSelection: null })}>Cancel</Button>
                        <Button
                            style={{ backgroundColor: '#3FB2BE', borderColor: '#3FB2BE' }}
                            onClick={this.handleAddFromMaster}
                            disabled={saving || !masterSelection || !masterSelection.product_name}
                        >
                            {saving ? <><i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>Adding...</> : 'Add & Select'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default POLineEditor;
